import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './LandingPage.css';
import logo from '../LogoBanaprecios.png';

const FEATURES_ICONS = ['📈', '📊', '📁', '🤝', '🔗', '💬'];
const STATS_VALUES = [
  { value: 52, suffix: ' sem' },
  { value: 4,  suffix: ' tipos' },
  { value: 100, suffix: '%' },
  { value: 24, suffix: '/7' },
];

function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCounter({ value, suffix, label }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const count = useCountUp(value, 1600, visible);

  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="lp-stat" ref={ref}>
      <div className="lp-stat-value">{count}{suffix}</div>
      <div className="lp-stat-label">{label}</div>
    </div>
  );
}

export default function LandingPage({ onLogin, onRegister }) {
  const { t } = useTranslation();
  const featuresRef = useRef(null);

  const FEATURES = [
    { icon: FEATURES_ICONS[0], title: t('lp_f1_title'), desc: t('lp_f1_desc') },
    { icon: FEATURES_ICONS[1], title: t('lp_f2_title'), desc: t('lp_f2_desc') },
    { icon: FEATURES_ICONS[2], title: t('lp_f3_title'), desc: t('lp_f3_desc') },
    { icon: FEATURES_ICONS[3], title: t('lp_f4_title'), desc: t('lp_f4_desc') },
    { icon: FEATURES_ICONS[4], title: t('lp_f5_title'), desc: t('lp_f5_desc') },
    { icon: FEATURES_ICONS[5], title: t('lp_f6_title'), desc: t('lp_f6_desc') },
  ];

  const STATS = [
    { ...STATS_VALUES[0], label: t('lp_stat_1_label') },
    { ...STATS_VALUES[1], label: t('lp_stat_2_label') },
    { ...STATS_VALUES[2], label: t('lp_stat_3_label') },
    { ...STATS_VALUES[3], label: t('lp_stat_4_label') },
  ];

  const scrollToFeatures = () => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="lp-root">

      {/* ── Navbar ── */}
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-nav-brand">
            <img src={logo} alt="BanaPrecios" className="lp-nav-logo" />
            <span className="lp-nav-name">BanaPrecios</span>
          </div>
          <div className="lp-nav-actions">
            <button className="lp-btn-ghost" onClick={onLogin}>{t('lp_login')}</button>
            <button className="lp-btn-primary" onClick={onRegister}>{t('lp_register_free')}</button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-bg">
          <div className="lp-orb lp-orb-1" />
          <div className="lp-orb lp-orb-2" />
          <div className="lp-orb lp-orb-3" />
        </div>

        <div className="lp-hero-inner">
          <div className="lp-hero-badge">
            <span className="lp-badge-dot" />
            {t('lp_badge')}
          </div>

          <h1 className="lp-hero-title">
            {t('lp_hero_title_pre')} <span className="lp-green">{t('lp_hero_title_highlight')}</span><br />
            {t('lp_hero_title_post')}
          </h1>

          <p className="lp-hero-sub">{t('lp_hero_sub')}</p>

          <div className="lp-hero-ctas">
            <button className="lp-btn-hero-primary" onClick={onRegister}>
              {t('lp_start_free')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
            <button className="lp-btn-hero-ghost" onClick={scrollToFeatures}>
              {t('lp_see_features')}
            </button>
          </div>

          {/* Ticker animado */}
          <div className="lp-ticker-wrap">
            <div className="lp-ticker-label">{t('lp_ticker_label')}</div>
            <div className="lp-ticker-track">
              <div className="lp-ticker-scroll">
                {['Caja $7.25 ↑', 'Kg 19.5 $0.372', 'Kg 18.8 $0.385', 'Cont. Paletizado', 'Granel', 'Bajo Cubierta', 'Caja $7.25 ↑', 'Kg 19.5 $0.372', 'Kg 18.8 $0.385', 'Cont. Paletizado', 'Granel', 'Bajo Cubierta'].map((t, i) => (
                  <span key={i} className="lp-ticker-item">{t}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Mini preview card */}
          <div className="lp-hero-card lp-float">
            <div className="lp-card-header">
              <span className="lp-card-dot lp-dot-green" />
              <span className="lp-card-dot lp-dot-yellow" />
              <span className="lp-card-dot lp-dot-red" />
              <span className="lp-card-title-mini">BanaPrecios — Semana 14</span>
            </div>
            <div className="lp-card-body">
              <div className="lp-card-row">
                <span>{t('lp_card_official')}</span>
                <span className="lp-card-val lp-green">$7.50 / caja</span>
              </div>
              <div className="lp-card-row">
                <span>{t('lp_card_boxes')}</span>
                <span className="lp-card-val">1,240</span>
              </div>
              <div className="lp-card-row">
                <span>{t('lp_card_amount')}</span>
                <span className="lp-card-val lp-green">$9,300.00</span>
              </div>
              <div className="lp-card-bar-wrap">
                <div className="lp-card-bar" style={{ width: '72%' }} />
              </div>
              <div className="lp-card-sub">{t('lp_card_sub')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section className="lp-stats-section">
        <div className="lp-stats-inner">
          {STATS.map((s, i) => (
            <StatCounter key={i} value={s.value} suffix={s.suffix} label={s.label} />
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section className="lp-features-section" ref={featuresRef}>
        <div className="lp-section-inner">
          <div className="lp-section-tag">{t('lp_features_tag')}</div>
          <h2 className="lp-section-title">{t('lp_features_title')}</h2>
          <p className="lp-section-sub">{t('lp_features_sub')}</p>

          <div className="lp-features-grid">
            {FEATURES.map((f, i) => (
              <div key={i} className="lp-feature-card" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cómo funciona ── */}
      <section className="lp-how-section">
        <div className="lp-section-inner">
          <div className="lp-section-tag">{t('lp_how_tag')}</div>
          <h2 className="lp-section-title">{t('lp_how_title')}</h2>

          <div className="lp-steps">
            <div className="lp-step">
              <div className="lp-step-num">1</div>
              <div className="lp-step-line" />
              <h3>{t('lp_step1_title')}</h3>
              <p>{t('lp_step1_desc')}</p>
            </div>
            <div className="lp-step">
              <div className="lp-step-num">2</div>
              <div className="lp-step-line" />
              <h3>{t('lp_step2_title')}</h3>
              <p>{t('lp_step2_desc')}</p>
            </div>
            <div className="lp-step">
              <div className="lp-step-num">3</div>
              <div className="lp-step-line lp-step-line--last" />
              <h3>{t('lp_step3_title')}</h3>
              <p>{t('lp_step3_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA Final ── */}
      <section className="lp-cta-section">
        <div className="lp-cta-inner">
          <div className="lp-cta-orb" />
          <h2 className="lp-cta-title">{t('lp_cta_title')}</h2>
          <p className="lp-cta-sub">{t('lp_cta_sub')}</p>
          <div className="lp-cta-actions">
            <button className="lp-btn-hero-primary" onClick={onRegister}>
              {t('lp_create_free')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
            <a
              href="mailto:info.banaprecios@gmail.com"
              className="lp-btn-hero-ghost"
            >
              {t('lp_contact_sales')}
            </a>
          </div>
          <p className="lp-cta-contact">{t('lp_cta_contact')}</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <img src={logo} alt="BanaPrecios" className="lp-footer-logo" />
            <span>BanaPrecios</span>
          </div>
          <p className="lp-footer-copy">
            {t('lp_footer_copy', { year: new Date().getFullYear() })}
          </p>
          <button className="lp-footer-login" onClick={onLogin}>{t('lp_footer_login')}</button>
        </div>
      </footer>

    </div>
  );
}
